import { Check, Plus, Minus, Star, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDiscoveryContext } from '../DiscoveryWizard'
import { formatPrice, getItemById } from '@/lib/pricing-data'
import { formatCurrency } from '@/lib/value-calculator'

export function SolutionStep() {
  const {
    recommendedSolution,
    quoteItems,
    totalInvestment,
    valueMetrics,
    addQuoteItem,
    removeQuoteItem,
  } = useDiscoveryContext()

  if (!recommendedSolution) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-muted-foreground">Building your solution...</p>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col items-center px-6 py-12 overflow-auto">
      <div className="max-w-4xl w-full space-y-8">
        {/* Header */}
        <div className="text-center wizard-fade-in">
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-3">
            Your recommended solution
          </h2>
          <p className="text-muted-foreground">
            Configured based on your requirements
          </p>
        </div>

        {/* Two column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recommended items */}
          <div className="lg:col-span-2 space-y-4">
            {/* License */}
            <RecommendedItem
              reasoning={recommendedSolution.reasoning.license}
              itemId={recommendedSolution.licenseId}
              isIncluded={quoteItems.some((qi) => qi.item.id === recommendedSolution.licenseId)}
              delay={0}
            />

            {/* Maps */}
            {recommendedSolution.mapsId ? (
              <RecommendedItem
                reasoning={recommendedSolution.reasoning.maps}
                itemId={recommendedSolution.mapsId}
                isIncluded={quoteItems.some((qi) => qi.item.id === recommendedSolution.mapsId)}
                onToggle={(add) => {
                  const item = getItemById(recommendedSolution.mapsId!)
                  if (item) {
                    if (add) {
                      addQuoteItem({ item, categoryId: 'maps', quantity: 1 })
                    } else {
                      removeQuoteItem(recommendedSolution.mapsId!)
                    }
                  }
                }}
                delay={100}
              />
            ) : (
              <InfoCard
                title="Map Coverage"
                message={recommendedSolution.reasoning.maps}
                delay={100}
              />
            )}

            {/* Support */}
            <RecommendedItem
              reasoning={recommendedSolution.reasoning.support}
              itemId={recommendedSolution.supportId}
              isIncluded={
                recommendedSolution.supportId === 'sup-std' ||
                quoteItems.some((qi) => qi.item.id === recommendedSolution.supportId)
              }
              onToggle={
                recommendedSolution.supportId !== 'sup-std'
                  ? (add) => {
                      const item = getItemById(recommendedSolution.supportId)
                      if (item) {
                        if (add) {
                          addQuoteItem({ item, categoryId: 'support', quantity: 1 })
                        } else {
                          removeQuoteItem(recommendedSolution.supportId)
                        }
                      }
                    }
                  : undefined
              }
              delay={200}
            />

            {/* Integration */}
            <RecommendedItem
              reasoning={recommendedSolution.reasoning.integration}
              itemId={recommendedSolution.integrationId}
              isIncluded={
                recommendedSolution.integrationId === 'int-self' ||
                quoteItems.some((qi) => qi.item.id === recommendedSolution.integrationId)
              }
              onToggle={
                recommendedSolution.integrationId !== 'int-self'
                  ? (add) => {
                      const item = getItemById(recommendedSolution.integrationId)
                      if (item) {
                        if (add) {
                          addQuoteItem({ item, categoryId: 'integration', quantity: 1 })
                        } else {
                          removeQuoteItem(recommendedSolution.integrationId)
                        }
                      }
                    }
                  : undefined
              }
              delay={300}
            />

            {/* R&D */}
            {recommendedSolution.rndId && recommendedSolution.reasoning.rnd && (
              <RecommendedItem
                reasoning={recommendedSolution.reasoning.rnd}
                itemId={recommendedSolution.rndId}
                isIncluded={quoteItems.some((qi) => qi.item.id === recommendedSolution.rndId)}
                onToggle={(add) => {
                  const item = getItemById(recommendedSolution.rndId!)
                  if (item) {
                    if (add) {
                      addQuoteItem({ item, categoryId: 'rnd', quantity: 1 })
                    } else {
                      removeQuoteItem(recommendedSolution.rndId!)
                    }
                  }
                }}
                delay={400}
              />
            )}
          </div>

          {/* Summary sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-4">
              {/* Investment summary */}
              <div className="p-6 rounded-lg border border-border/30 bg-secondary/10 wizard-fade-in">
                <h3 className="text-sm font-medium text-muted-foreground mb-4">
                  Investment Summary
                </h3>
                <div className="space-y-3">
                  {quoteItems.map((qi) => (
                    <div key={qi.item.id} className="flex justify-between text-sm">
                      <span className="text-muted-foreground truncate pr-2">
                        {qi.item.name}
                      </span>
                      <span className="text-foreground font-medium">
                        {formatPrice(qi.item.price * qi.quantity)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-border/30 pt-3 mt-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-foreground">Total</span>
                      <span className="text-lg font-bold text-primary">
                        {formatCurrency(totalInvestment)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ROI reminder */}
              {valueMetrics && (
                <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 wizard-fade-in">
                  <p className="text-xs text-muted-foreground mb-1">Projected Annual Value</p>
                  <p className="text-xl font-bold text-primary">
                    {formatCurrency(valueMetrics.totalAnnualValue)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {valueMetrics.threeYearROI}% ROI over 3 years
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface RecommendedItemProps {
  reasoning: string
  itemId: string
  isIncluded: boolean
  onToggle?: (add: boolean) => void
  delay: number
}

function RecommendedItem({
  reasoning,
  itemId,
  isIncluded,
  onToggle,
  delay,
}: RecommendedItemProps) {
  const item = getItemById(itemId)
  if (!item) return null

  const isFree = item.price === 0 || item.included

  return (
    <div
      className={cn(
        'p-4 rounded-lg border transition-all wizard-stagger',
        isIncluded
          ? 'border-primary/30 bg-primary/5'
          : 'border-border/30 bg-secondary/10'
      )}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono text-muted-foreground/60">
              {item.partNumber}
            </span>
            <h4 className="text-sm font-medium text-foreground">{item.name}</h4>
            {item.recommended && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/20 text-primary">
                <Star className="w-2.5 h-2.5" />
                Recommended
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground/60 mb-2">{item.description}</p>
          <p className="text-xs text-muted-foreground">{reasoning}</p>
        </div>

        <div className="flex-shrink-0 text-right">
          <p className="text-sm font-medium text-foreground">
            {isFree ? 'Included' : formatPrice(item.price)}
          </p>
          {onToggle && !isFree && (
            <button
              onClick={() => onToggle(!isIncluded)}
              className={cn(
                'mt-2 p-1.5 rounded-md transition-colors',
                isIncluded
                  ? 'bg-primary/20 text-primary hover:bg-primary/30'
                  : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
              )}
            >
              {isIncluded ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </button>
          )}
          {isIncluded && !onToggle && (
            <div className="mt-2 p-1.5">
              <Check className="w-4 h-4 text-primary" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface InfoCardProps {
  title: string
  message: string
  delay: number
}

function InfoCard({ title, message, delay }: InfoCardProps) {
  return (
    <div
      className="p-4 rounded-lg border border-border/30 bg-secondary/10 wizard-stagger"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start gap-3">
        <Info className="w-4 h-4 text-muted-foreground mt-0.5" />
        <div>
          <h4 className="text-sm font-medium text-foreground mb-1">{title}</h4>
          <p className="text-xs text-muted-foreground/70">{message}</p>
        </div>
      </div>
    </div>
  )
}

